import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard, Bus, UserCog, Handshake, Users, Route, CalendarDays,
  Megaphone, Star, ReceiptText, Ticket, LogOut, Wallet, ChevronRight,
  UsersRound, Settings as SettingsIcon, Sun, Moon, ScrollText, Menu, X, ClipboardList
} from "lucide-react";

const nav = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", roles: ["admin","subadmin","operator","accounts"] },
  { to: "/vehicles", icon: Bus, label: "Vehicles", roles: ["admin","subadmin","operator"] },
  { to: "/drivers", icon: UserCog, label: "Drivers", roles: ["admin","subadmin","operator"] },
  { to: "/driver-admin", icon: UserCog, label: "Driver Assignments", roles: ["admin","subadmin","operator","accounts"] },
  { to: "/vendors", icon: Handshake, label: "Vendors", roles: ["admin","subadmin","operator","accounts"] },
  { to: "/clients", icon: Users, label: "Clients", roles: ["admin","subadmin","operator","accounts"] },
  { to: "/client-assignments", icon: ScrollText, label: "Client Assignments", roles: ["admin","subadmin","operator","accounts"] },
  { to: "/trips", icon: Route, label: "Trips & Accounts", roles: ["admin","subadmin","operator","accounts"] },
  { to: "/vendor-accounting", icon: Handshake, label: "Vendor Accounting", roles: ["admin","subadmin","operator","accounts","vendor"] },
  { to: "/calendar", icon: CalendarDays, label: "Assignment Calendar", roles: ["admin","subadmin","operator"] },
  { to: "/leads", icon: Megaphone, label: "Leads", roles: ["admin","subadmin","operator"] },
  { to: "/reviews", icon: Star, label: "Reviews", roles: ["admin","subadmin"] },
  { to: "/invoices", icon: ReceiptText, label: "Invoices", roles: ["admin","subadmin","accounts"] },
  { to: "/lottery", icon: Ticket, label: "Lottery", roles: ["admin","subadmin"] },
  { to: "/sales-agents", icon: Megaphone, label: "Sales & Marketing", roles: ["admin","subadmin","accounts"] },
  { to: "/driver-submissions", icon: ClipboardList, label: "Driver Submissions", roles: ["admin","subadmin","operator","accounts"] },
  { to: "/users", icon: UsersRound, label: "Users & Roles", roles: ["admin","subadmin"] },
  { to: "/driver-portal", icon: Wallet, label: "My Driver Portal", roles: ["driver"] },
  { to: "/client-portal", icon: Users, label: "My Trips", roles: ["client"] },
  { to: "/sales-portal", icon: Megaphone, label: "My Sales Dashboard", roles: ["sales"] },
  { to: "/settings", icon: SettingsIcon, label: "Settings", roles: ["admin","subadmin","operator","accounts","driver","vendor","client","sales"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Close drawer whenever route changes
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  const items = nav.filter((n) => {
    if (!n.roles.includes(user?.role)) return false;
    const sec = (n.to || "").replace("/", "").replace("-portal", "");
    const allowed = user?.sections;
    if (Array.isArray(allowed) && allowed.length && !["driver-portal","client-portal","settings"].includes(n.to.slice(1))) {
      return allowed.includes(sec);
    }
    return true;
  });
  const [settings, setSettings] = useState({});
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const Sidebar = (
    <>
      <Link to="/dashboard" className="px-5 py-5 flex items-center gap-3 border-b border-border" data-testid="brand-link">
        {settings.logo_url ? (
          <img src={settings.logo_url} alt="logo" className="w-9 h-9 rounded-lg object-cover"/>
        ) : (
          <div className="w-9 h-9 rounded-lg bg-amber-500 grid place-items-center text-[hsl(224_60%_6%)] font-black font-display">
            {(settings.brand_name || "A")[0]}
          </div>
        )}
        <div className="min-w-0">
          <div className="font-display font-bold text-base leading-tight truncate">{settings.brand_name || "Ashok NE Travels"}</div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{settings.tagline || "Fleet Control"}</div>
        </div>
      </Link>
      <nav className="p-3 space-y-0.5 flex-1 overflow-y-auto">
        {items.map((it) => (
          <NavLink
            key={it.to} to={it.to} data-testid={`nav-${it.to.replace("/", "")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                isActive ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                : "text-slate-300 hover:text-white hover:bg-white/5"}`}
          >
            <it.icon className="w-4 h-4 shrink-0" />
            <span className="truncate">{it.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="px-2 py-2 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-slate-700 overflow-hidden grid place-items-center font-bold text-sm shrink-0">
            {user?.photo_url ? <img src={user.photo_url} alt="" className="w-full h-full object-cover"/> : (user?.name?.[0]?.toUpperCase() || "U")}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm truncate">{user?.name}</div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{user?.role}</div>
          </div>
          <button
            data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
            className="p-2 rounded-md hover:bg-white/5 text-slate-400 hover:text-red-400 transition-colors" title="Logout">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen grain flex bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="w-64 shrink-0 border-r border-border bg-[hsl(224_45%_8%)] hidden md:flex flex-col">
        {Sidebar}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setMobileOpen(false)} data-testid="mobile-drawer-backdrop"/>
          <aside className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] border-r border-border bg-[hsl(224_45%_8%)] flex flex-col animate-in slide-in-from-left duration-200">
            <button onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 p-2 rounded-md text-slate-400 hover:text-white hover:bg-white/5"
              data-testid="mobile-drawer-close" aria-label="Close menu">
              <X className="w-5 h-5"/>
            </button>
            {Sidebar}
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 relative z-10">
        <header className="glass sticky top-0 z-30 px-4 md:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
              className="md:hidden p-2 rounded-md border border-border hover:border-amber-500/40">
              <Menu className="w-4 h-4"/>
            </button>
            <div className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground min-w-0">
              <span className="badge-live">Live</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-slate-300 truncate">{user?.role} view</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs min-w-0">
            <button
              data-testid="theme-toggle" onClick={toggle}
              className="p-2 rounded-md border border-border hover:border-amber-500/40 transition-colors shrink-0" title="Toggle theme">
              {theme === "dark" ? <Sun className="w-4 h-4 text-amber-300"/> : <Moon className="w-4 h-4 text-slate-600"/>}
            </button>
            <span className="hidden lg:inline text-muted-foreground">Signed in as</span>
            <span className="hidden sm:inline font-mono text-amber-300 max-w-[180px] md:max-w-none truncate">{user?.email}</span>
          </div>
        </header>
        <div className="p-4 md:p-6 lg:p-8 max-w-[1600px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
